import { useState, useEffect } from 'react';
import firebase from 'firebase/app';

const MAX_OWNED = 20;

/**
 * The Strike clients a person can act for: the ones they own (`clients.ownerId == uid`)
 * plus the ones delegated to them (`users/{uid}/client_roles/{clientId}`, backend-mirrored).
 * Same two sources as useTeamAdminAccess.accessibleClientIds, resolved once to full docs
 * so a screen can show names. Each source fails closed on its own.
 *
 * Returns `null` while loading, then `[{ id, displayName, clientType, avatar, via }]`
 * where `via` is `'owner'` or `'delegate'`.
 */
export function useAccessibleClients(uid) {
  const [clients, setClients] = useState(null);

  useEffect(() => {
    if (!uid) {
      setClients(null);
      return undefined;
    }
    let active = true;
    const db = firebase.firestore();

    const load = async () => {
      const byId = new Map();
      try {
        const own = await db.collection('clients').where('ownerId', '==', uid).limit(MAX_OWNED).get();
        own.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data(), via: 'owner' }));
      } catch (err) {
        /* fail closed per-source */
      }
      try {
        const grants = await db.collection('users').doc(uid).collection('client_roles').get();
        const missing = grants.docs.map((d) => d.id).filter((id) => !byId.has(id));
        const docs = await Promise.all(missing.map((id) => db.collection('clients').doc(id).get()));
        docs.forEach((d) => {
          if (d.exists) {
            byId.set(d.id, { id: d.id, ...d.data(), via: 'delegate' });
          }
        });
      } catch (err) {
        /* fail closed per-source */
      }
      if (active) {
        setClients([...byId.values()]);
      }
    };

    load();
    return () => { active = false; };
  }, [uid]);

  return clients;
}

export default useAccessibleClients;
